/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/**
@format
@flow
*/

import React, { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  setupCompanyVideos, setupProfileImage, getInitials, withAppManager, useNotification, LoadingSpinner, formatDate,
} from '@ziynx/components/utilities';
import Headline from '@ziynx/components/headlines';
import Card from '@ziynx/components/cards';
import Icon from '@ziynx/components/icons';
import Link from '@ziynx/components/links';
import { ContentContainer } from '@ziynx/components/layout';
import { Heading, Body } from '@ziynx/components/typography';
import Colors from '@ziynx/theme/colors';
import ActionTypes from '@ziynx/action-types';
import { eventNames } from '@ziynx/analytics';
import type { MongoIdType } from '@ziynx/flow-types/globals';
import type { RefinedCompanyVideos, RefinedProfileImage } from '@ziynx/flow-types/companyProfile';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

type Company = {
  _id?: MongoIdType,
  name: string,
  headline: string,
  industries: Array<{
    name: string,
  }>,
  companySize: string,
  success: boolean,
  patching: boolean,
  patchSuccess: boolean,
  patchedImage: boolean,
  checking: boolean,
  profileImage?: RefinedProfileImage,
  videos: Array<RefinedCompanyVideos>,
  canEdit?: boolean,
};

type Location = {
  state: {
    from: string,
    profileUpdated: boolean,
  }
}

type Props = $ReadOnly<{|
  history: {
    push: (pathName: string, params?: { from: string }) => void,
    goBack: () => void,
    replace: () => void,
    location: Location,
    state: Object,
  },
|}>;

const PageWrapper = styled.div`
  padding: ${({ isMobile }) => (isMobile ? '48px 16px' : '40px')};
  width: 100%;
  box-sizing: border-box;
`;

const StyledHeadline = styled(Headline)`
  margin-bottom: 24px;
`;

const EmptyWrapper = styled.div`
  text-align: center;
`;

const IconWrapper = styled.div`
  margin-bottom: 16px;
`;

const TextWrapper = styled.div`
  margin-bottom: 4px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 688px;
  padding-top: 134px;
`;

const StyledCard = styled(Card)`
  ${({ isMobile }) => isMobile && 'margin: auto;'}
  ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom}px;`}
`;

const GroupWrapper = styled.div`
  ${({ isMobile }) => isMobile && 'margin: auto;'}
  max-width: 688px;
`;

const ContentWrapper = styled.div`
  padding-bottom: 64px;
`;

const CompanyProfile = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;
  const dispatch = useDispatch();
  const location: Location = useLocation();
  const { state } = location;
  const { notificationDispatch } = useNotification();
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';

  const resetRouteState = (): void => {
    if (state?.profileUpdated) history.replace();
  };

  useEffect(() => {
    if (history?.location?.state?.profileUpdated) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Your info has been updated.',
        },
      });
    }
    resetRouteState();
    dispatch({
      type: ActionTypes.company.reset,
      id: 'me',
    });
  }, [history?.state?.profileUpdated]);

  const companyService: Company = useSelector(({ company }) => {
    const myCompany = company?.me || {};
    const videoArray = setupCompanyVideos(myCompany?.videos);
    const profileImage: RefinedProfileImage = setupProfileImage(myCompany?.profileImage);

    return {
      ...myCompany,
      profileImage,
      videos: videoArray,
    };
  }, shallowEqual);

  const statementService = useSelector(({ statements }) => statements || {}, shallowEqual);
  const jobService = useSelector(({ job }) => job || {}, shallowEqual);

  const dispatchGet = useCallback(() => {
    // enter page with cleared reducer.
    dispatch({ type: ActionTypes.company.get.START, id: 'me' });
    dispatch({
      type: ActionTypes.statements.getAll.START,
      id: 'me',
    });
    dispatch({
      type: ActionTypes.job.getAllJobs.START,
      id: 'me',
    });
    dispatch({
      type: ActionTypes.company.reset,
      id: 'me',
    });
  }, [companyService?.patchedImage]);

  useEffect(() => {
    dispatchGet();
  }, []);

  useEffect(() => {
    if (statementService?.removalSuccess && !statementService?.removing) {
      dispatchGet();
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Content has been removed successfully.',
        },
      });
    }
    if (statementService?.removalError && !statementService?.removing) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Something went wrong. Please try again.',
        },
      });
    }
  }, [statementService?.removing, statementService?.removalSuccess, statementService?.removalError]);

  useEffect(() => {
    if (history?.location?.state?.from) {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames?.navigated_company_profile,
        properties: {
          referrer: history?.location?.state?.from,
        },
      });
    } else {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames?.navigated_company_profile,
      });
    }
  }, [history?.location?.state?.from]);

  useEffect(() => {
    if (jobService?.removalSuccess && !jobService?.removing) {
      dispatchGet();
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Content has been removed successfully.',
        },
      });
    }
    if (jobService?.removalError && !jobService?.removing) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Something went wrong. Please try again.',
        },
      });
    }
  }, [jobService?.removing, jobService?.removalSuccess, jobService?.removalError]);

  const info = {
    heading: companyService?.name,
    squircleText: getInitials(companyService?.name),
    imageSource: `${companyService?.profileImage?.image?.uri ?? ''}`,
    textLink: companyService?.canEdit ? {
      children: 'Edit',
      onClick: () => {
        // Log event
        history.push('/company-profile/edit');
      },
    } : undefined,
  };

  const headline = {
    label: 'Headline',
    input: `${companyService?.headline ?? ''}`,
    link: companyService?.canEdit ? {
      children: 'Add a headline',
      onClick: () => {
        history.push('/company-profile/edit');
      },
    } : undefined,
  };
  const industries = {
    label: 'Industry',
    input: `${(companyService?.industries ?? [])[0]?.name ?? ''}`,
    link: {
      children: 'Add industries',
      onClick: () => {
        history.push('/company-profile/edit');
      },
    },
  };
  const companySize = {
    label: 'No. of employees',
    input: `${companyService?.companySize ?? ''}`,
  };

  const details = [headline, industries];

  if (companyService?.companySize) details.push(companySize);

  const videos = [
    { ...companyService?.videos[0], title: companyService?.videos[0]?.title || 'Video 1' },
    { ...companyService?.videos[1], title: companyService?.videos[1]?.title || 'Video 2' },
    { ...companyService?.videos[2], title: companyService?.videos[2]?.title || 'Video 3' },
  ];

  // Questions

  const questionsLink = `See all (${statementService?.total})`;
  const jobsLink = `See all (${jobService?.total})`;

  const deleteQuestion = (questionId) => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.deleted_question,
      properties: {
        questionId,
      },
    });
    dispatch({
      type: ActionTypes.statements.removeQuestion.START,
      id: questionId,
    });
  };

  const deleteJob = (jobId) => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.deleted_job_post_company_profile,
      properties: {
        jobId,
      },
    });
    dispatch({
      type: ActionTypes.job.removeJob.START,
      id: jobId,
    });
  };

  const renderEmptyQuestions = () => (
    <EmptyWrapper>
      <IconWrapper>
        <Icon name="info-fill" small color={Colors.GrayLight} />
      </IconWrapper>
      <TextWrapper>
        <Heading medium color={Colors.Gray}>No questions</Heading>
      </TextWrapper>
      {!(statementService?.results ?? []).length
      && (
        <Body medium>
          <Link
            variant="text"
            onClick={() => {
            // Log event
              history.push('/content/video-question', { from: 'company-profile' });
            }}
            large
          >
            Add new

          </Link>
        </Body>
      )}
    </EmptyWrapper>
  );

  const renderQuestionCards = () => statementService?.results?.map((item, index) => {
    if (!item) return null;
    if (index > 2) return null;
    const editQuestionPath = '/content/video-question/';
    const questionUrl = editQuestionPath.concat(item?._id);
    const lastChild = (index === statementService?.results?.length - 1) || index === 2;
    // eslint-disable-next-line prefer-template
    const postDate = 'Posted ' + formatDate(item?.createdAt);

    return (
      <StyledCard
        key={index}
        marginBottom={lastChild ? 0 : 24}
        headline={index === 0 ? {
          children: 'Our questions',
          textLink: {
            children: questionsLink,
            onClick: () => {
              history.push('/content');
            },
          },
        } : undefined}
        variant="squircle"
        squircle={{
          iconName: 'play-circle',
          color: Colors.White,
          backgroundColor: Colors.GrayVeryDark,
          inverted: false,
        }}
        rightAction={{
          popover: {
            position: 'bottomLeft',
            links: [
              {
                children: 'Edit question',
                large: true,
                onClick: () => history.push(questionUrl, { from: 'company-profile' }),
              },
              // {
              //   children: item?.archived ? 'Unarchive question' : 'Archive question',
              //   large: true,
              //   color: Colors.SecondaryRed,
              //   onClick:
              //   item?.archived
              //     ? (() => clickArchiveLink(item?._id, 'unarchive'))
              //     : (() => clickArchiveLink(item?._id, 'archive')),
              // },
              {
                children: 'Delete question',
                large: true,
                color: Colors.SecondaryRed,
                onClick: () => {
                  deleteQuestion(item?._id);
                },
              },
            ],
          },
        }}
        title={item?.question}
        body={postDate}
        archived={item?.archived}
        viewport={viewport}
      />
    );
  });

  const renderQuestions = () => {
    if (!(statementService?.results ?? []).length
    && (!statementService?.fetching
    && statementService?.success)) {
      return (
        <>
          <StyledHeadline variant="default" title={{ text: 'Our questions' }} />
          <Card variant="list" renderEmpty={renderEmptyQuestions} />
        </>
      );
    }
    return renderQuestionCards();
  };

  const renderEmptyJobs = () => (
    <EmptyWrapper>
      <IconWrapper>
        <Icon name="post-fill" small color={Colors.GrayLight} />
      </IconWrapper>
      <TextWrapper>
        <Heading medium color={Colors.Gray}>No job posts</Heading>
      </TextWrapper>
      {!(jobService?.results ?? []).length
      && (
        <Body medium>
          <Link
            variant="text"
            onClick={() => {
            // Log event
              dispatch({
                type: ActionTypes.analytics.logEvent.START,
                name: eventNames.navigated_add_new_job_post,
                properties: {
                  referrer: 'addEmptyCTA',
                },
              });
              history.push('/jobs/create-post', { from: 'company-profile' });
            }}
            large
          >
            Add new

          </Link>
        </Body>
      )}
    </EmptyWrapper>
  );

  const renderJobCards = () => jobService?.results?.map((item, index) => {
    if (!item) return null;
    if (index > 2) return null;
    const editJobPath = '/jobs/create-post/';
    const jobUrl = editJobPath.concat(item?._id);
    const locations = () => {
      const length = item?.locations?.length;
      if (!length && !item?.remote) return undefined;
      if (item?.remote) return 'Remote';
      if (length === 1) return item?.locations[0]?.name;
      if (length > 1) return 'Multiple locations';
    };
    // eslint-disable-next-line prefer-template
    const postDate = 'Posted ' + formatDate(item?.createdAt);

    return (
      <StyledCard
        key={index}
        marginBottom={24}
        headline={index === 0 ? {
          children: 'Our job posts',
          textLink: {
            children: jobsLink,
            onClick: () => history.push('/jobs', { from: 'company-profile' }),
          },
        } : undefined}
        variant="squircle"
        squircle={{
          iconName: 'post-fill',
          color: Colors.White,
          backgroundColor: Colors.GrayVeryDark,
          inverted: false,
        }}
        rightAction={{
          popover: {
            position: 'bottomLeft',
            links: [
              {
                children: 'Edit job post',
                large: true,
                onClick: () => history.push(jobUrl, { from: 'company-profile' }),
              },
              // {
              //   children: item?.archived ? 'Unarchive question' : 'Archive question',
              //   large: true,
              //   color: Colors.SecondaryRed,
              //   onClick:
              //   item?.archived
              //     ? (() => clickArchiveLink(item?._id, 'unarchive'))
              //     : (() => clickArchiveLink(item?._id, 'archive')),
              // },
              {
                children: 'Delete job post',
                large: true,
                color: Colors.SecondaryRed,
                onClick: () => deleteJob(item?._id),
              },
            ],
          },
        }}
        title={item?.title}
        subtitle={locations()}
        body={postDate}
        archived={item?.archived}
        viewport={viewport}
      />
    );
  });

  const renderJobs = () => {
    if (!(jobService?.results ?? []).length
    && (!jobService?.fetching
    && jobService?.success)) {
      return (
        <>
          <StyledHeadline variant="default" title={{ text: 'Our job posts' }} />
          <Card variant="list" renderEmpty={renderEmptyJobs} />
        </>
      );
    }
    return renderJobCards();
  };

  return (
    <>
      <Helmet>
        <title>{`${companyService?.name ?? 'Company Profile'} | ZIYNX`}</title>
      </Helmet>
      <ContentContainer>
        <PageWrapper isMobile={isMobile}>
          <ContentWrapper>
            <StyledCard
              isMobile={isMobile}
              variant="companyDetails"
              info={info}
              details={details}
            />
          </ContentWrapper>
          <ContentWrapper>
            <GroupWrapper isMobile={isMobile}>
              <Card
                variant="videoSquareGroup"
                headlineLocation="bottom"
                headline={{
                  title: {
                    text: 'Our videos',
                  },
                  textLink: companyService?.canEdit
                    ? {
                      children: 'Edit',
                      onClick: () => {
                        dispatch({
                          type: ActionTypes.analytics.logEvent.START,
                          name: eventNames.navigated_edit_company_profile_videos,
                          properties: {
                            referrer: 'Video Edits CTA',
                          },
                        });
                        history.push('/company-profile/edit/videos');
                      },
                    } : undefined,
                }}
                data={videos}
                viewport={viewport}
              />
            </GroupWrapper>
          </ContentWrapper>
          <ContentWrapper>
            {statementService?.fetching && !(statementService?.results ?? []).length
              ? (
                <SpinnerWrapper>
                  <LoadingSpinner />
                </SpinnerWrapper>
              )
              : renderQuestions()}
          </ContentWrapper>
          <ContentWrapper>
            {jobService?.fetching && !(jobService?.results ?? []).length
              ? (
                <SpinnerWrapper>
                  <LoadingSpinner />
                </SpinnerWrapper>
              )
              : renderJobs()}
          </ContentWrapper>
        </PageWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(CompanyProfile): React$ComponentType<ManagedProps<Props>>);
